<template>
  <section>
    <div class="flex items-center justify-between mb-4">
      <h4 class="mb-2">
        {{ $t("stats.course.sidebar.objectives") }}
      </h4>
      <Button
        :label="$t('labels.generic', { pt: 'Baixar', en: 'Download' })"
        icon="pi pi-download"
        class="p-button-sm btn-primary h-9"
        :loading="btnLoading"
        @click="downloadCSV"
      />
    </div>
    <div class="bg-white rounded-lg border border-gray-300">
      <Table
        :slotData="objectives"
        :actions="actions"
        :columns="columns"
        :loading="loading"
        :filters="filters"
        :params="params"
        :hasActions="isAdaptive"
        @onGoTo="(e) => goTo(e)"
        @onFilter="filterFor()"
        @onClearFilter="clearFilter()"
        @onToggleFilter="(e, type) => toggleFilter(e, type)"
        @onPaginate="paginate($event)"
      >
        <template #filters>
          <InputText
            v-if="filterType === 'chapterName'"
            v-model="filters.chapters"
          />
          <InputText
            v-if="filterType === 'learningObjectiveName'"
            v-model="filters.objectives"
          />
        </template>
      </Table>
    </div>
  </section>
</template>

<script lang="ts">
import { QueryParams } from "@/models/query-params.model";
import { StatsRest } from "@/services/rest/stats.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { saveAs } from "file-saver";
import { delay } from "rxjs";
import { defineComponent } from "vue";
import { StatsService } from "../../stats.service";
import { ObjectiveService } from "./objectives.service";
import { columns } from "./objectives.utils";

export default defineComponent({
  name: "objectives",
  computed: {
    rest(): StatsRest {
      return new StatsRest();
    },
    service(): ObjectiveService {
      return new ObjectiveService();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
    isAdaptive(): boolean {
      return JSON.parse(localStorage.getItem("isAdaptiveStats") as string);
    },
  },
  data() {
    return {
      courseId: this.$route.params.courseId as string,
      columns: columns as any,
      loading: true as boolean,
      btnLoading: false as boolean,
      objectives: [] as any,
      params: new QueryParams(),
      filters: {
        isCleareable: false,
        chapters: null,
        objectives: null,
      } as any,
      actions: [
        {
          title: "Ver objetivo",
          icon: "hi-chevron-right",
          type: "goTo",
          style: "btn-outline",
        },
      ],
      filterType: "" as string,
      classroomId: undefined as string | undefined,
    };
  },
  mounted() {
    this.objectivesPaginated();
    this.getClassroomId();
  },
  watch: {
    classroomId(val, old) {
      if (val !== undefined || val !== old) this.objectivesPaginated();
    },
  },
  methods: {
    paginate(event: { page: number; rows: number }): void {
      const params = new QueryParams(event.page, event.rows);
      params.learningObjectiveName = this.filters.objectives;
      params.chapterName = this.filters.chapters;
      this.setPage(params);
    },
    setPage(queryParams: QueryParams): void {
      this.loading = true;
      queryParams.classroomId = this.classroomId ?? undefined;

      this.service.objectives.pipe(delay(500)).subscribe({
        next: (response: any) => {
          this.objectives = response.content ?? [];
          this.params.total = response.total;
          this.loading = false;
        },
      });
      this.service.getObjectivesFromCourse(this.courseId, queryParams);
    },
    objectivesPaginated(): void {
      const page = this.params.page;
      const size = this.params.size;
      const params = new QueryParams(page, size);
      this.setPage(params);
    },
    toggleFilter(event: any, type: string): void {
      this.filterType = type;
    },
    filterFor(): void {
      this.filters.isCleareable = this.isCleareable();
      var params = new QueryParams();
      params.learningObjectiveName = this.filters.objectives;
      params.chapterName = this.filters.chapters;
      if (this.filterType == "learningObjectiveName") {
        this.setPage(params);
      } else if (this.filterType == "chapterName") {
        this.setPage(params);
      }
    },
    clearFilter(): void {
      this.filters.isCleareable = false;
      this.filters.objectives = null;
      this.filters.chapters = null;
      this.objectivesPaginated();
    },
    isCleareable(): boolean {
      return this.filters.chapters !== "" || this.filters.objectives !== "";
    },
    downloadCSV(): void {
      this.btnLoading = true;
      this.rest
        .getObjectivesStatsCsv(this.courseId, { classroomId: this.classroomId })
        .pipe(delay(1000))
        .subscribe({
          next: (response) => {
            this.btnLoading = false;
            const blob = new Blob([response], { type: "text/csv" });
            const url = window.URL.createObjectURL(blob);
            saveAs(url, `objectives_data.csv`);
          },
          error: () => {
            this.btnLoading = false;
          },
        });
    },
    goTo(objective: any): void {
      this.go.redirectToPage("statsObjective", {
        objectiveId: objective.learningObjectiveId,
        courseId: this.courseId,
        objectiveName: objective.learningObjectiveName,
      });

      if (objective.classroomId) {
        localStorage.setItem("objClassroomId", objective.classroomId);
        localStorage.setItem(
          "objClassroomName",
          JSON.stringify(objective.classroomName)
        );
      } else {
        localStorage.removeItem("objClassroomId");
        localStorage.removeItem("objClassroomName");
      }
    },
    getClassroomId(): void {
      this.loading = true;
      StatsService.classroom.subscribe({
        next: (e) => (this.classroomId = e),
      });
    },
  },
});
</script>
